import {
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Navigate,
  stripSearchParams,
} from "@tanstack/react-router";
import axios from "axios";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { ApiErrors } from "../../../../@types/components/organisms/ApiErrors";
import {
  destroyCreatePurchaseOrderBySpot,
  generateCreatePurchaseOrderBySpot,
  invalidateListCreatePurchaseOrderBySpots,
  reloadCreatePurchaseOrderBySpotFromSap,
  useShowCreatePurchaseOrderBySpot,
} from "../../../../__generated__/orval/endpoints/create-purchase-order-by-spots/create-purchase-order-by-spots";
import { EntitiesCreatePurchaseOrderBySpotMemberMain } from "../../../../__generated__/orval/models/entitiesCreatePurchaseOrderBySpotMemberMain.zod";
import { ApiErrorsTable } from "../../../../components/organisms/ApiErrorsTable";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { getMicrosoftToken } from "../../../../services/getMicrosoftToken";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { SpotPurchaseOrderPreviewModal } from "../-internal_components/SpotPurchaseOrderPreviewModal";
import { spotSearchSchema } from "../index";

const actionErrorSchema = z.object({
  response: z.object({ data: z.object({ error: z.string() }) }),
});

const formatNumber = (value: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    maximumFractionDigits: 3,
    minimumFractionDigits: 2,
  }).format(Number(value));

const ExecutionDetail = () => {
  const { createPurchaseOrderBySpotId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("items");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [expandedExecution, setExpandedExecution] = useState<string | null>(
    null,
  );
  const { data, error, isPending, isError, refetch } =
    useShowCreatePurchaseOrderBySpot(createPurchaseOrderBySpotId, {
      query: {
        refetchInterval: (query) => {
          const batch = EntitiesCreatePurchaseOrderBySpotMemberMain.safeParse(
            query.state.data,
          );
          return batch.success &&
            (batch.data.sap_load_status === "loading" ||
              batch.data.execution_status === "processing" ||
              batch.data.spot_items.some(
                (item) => item.purchase_order_link_pending,
              ))
            ? 5000
            : false;
        },
      },
    });
  const parsed = EntitiesCreatePurchaseOrderBySpotMemberMain.safeParse(data);
  const canGenerate = Boolean(
    user?.roles_names.includes("Spot - Generate Purchase Order"),
  );
  const isPendingExecution =
    parsed.success && parsed.data.execution_status === "pending";
  const sapLoadStatus = parsed.success ? parsed.data.sap_load_status : null;
  const isSapReady = isPendingExecution && sapLoadStatus === "ready";
  const isSapFailed = isPendingExecution && sapLoadStatus === "failed";
  const failedAttemptNotSubmitted = Boolean(
    parsed.success &&
      parsed.data.execution_status === "failed" &&
      parsed.data.can_delete,
  );
  const canDeleteFailedAttempt = Boolean(
    canGenerate && failedAttemptNotSubmitted,
  );
  const canRetry = Boolean(
    canGenerate &&
      parsed.success &&
      parsed.data.execution_status === "failed" &&
      parsed.data.can_retry,
  );

  const handleActionError = (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      setNotFound(true);
      return;
    }

    const parsedError = actionErrorSchema.safeParse(error);
    setActionError(
      parsedError.success
        ? parsedError.data.response.data.error
        : t("spotPurchaseOrder.actionError"),
    );
  };

  const handleGenerate = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      if (!(await getMicrosoftToken("procurement"))) return;
      await generateCreatePurchaseOrderBySpot(createPurchaseOrderBySpotId);
      setPreviewOpen(false);
      await refetch();
    } catch (error) {
      handleActionError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReload = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      await reloadCreatePurchaseOrderBySpotFromSap(createPurchaseOrderBySpotId);
      await refetch();
    } catch (error) {
      handleActionError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      await destroyCreatePurchaseOrderBySpot(createPurchaseOrderBySpotId);
      await invalidateListCreatePurchaseOrderBySpots(queryClient);
      navigate({
        search: { ...search, upload: true },
        to: "/create_purchase_order_by_spots",
      });
    } catch (error) {
      handleActionError(error);
    } finally {
      setActionLoading(false);
    }
  };

  if (
    notFound ||
    (axios.isAxiosError(error) && error.response?.status === 404)
  ) {
    return <Navigate to="/not_found" />;
  }

  return (
    <PageContainer showBackButton title={t("spotPurchaseOrder.title")}>
      {!!actionError && !previewOpen && (
        <Alert severity="error">{actionError}</Alert>
      )}
      {isError || (!isPending && !parsed.success) ? (
        <Alert severity="error">{t("spotPurchaseOrder.loadError")}</Alert>
      ) : isPending || !parsed.success ? (
        <Skeleton height={120} />
      ) : (
        <TabContext value={tab}>
          {isPendingExecution && sapLoadStatus === "loading" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("spotPurchaseOrder.sapLoading")}
            </Alert>
          )}
          {!!isSapFailed && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parsed.data.sap_load_error}
              <br />
              {t("spotPurchaseOrder.sapLoadFailed")}
            </Alert>
          )}
          {!!isSapReady && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("spotPurchaseOrder.pendingInfo")}
            </Alert>
          )}
          {!!failedAttemptNotSubmitted && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("spotPurchaseOrder.failedBeforeSubmissionInfo")}
            </Alert>
          )}
          {!!(
            canGenerate &&
            (isSapReady || isSapFailed || canRetry || canDeleteFailedAttempt)
          ) && (
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              {!!isSapReady && (
                <Button
                  disabled={actionLoading}
                  onClick={() => setPreviewOpen(true)}
                  variant="contained"
                >
                  {t("spotPurchaseOrder.reviewAndGenerate")}
                </Button>
              )}
              {!!isSapFailed && (
                <Button
                  disabled={actionLoading}
                  onClick={handleReload}
                  variant="outlined"
                >
                  {t("spotPurchaseOrder.sapReload")}
                </Button>
              )}
              {!!(isSapReady || isSapFailed || canDeleteFailedAttempt) && (
                <Button
                  color="error"
                  disabled={actionLoading}
                  onClick={handleDelete}
                  variant="outlined"
                >
                  {t("spotPurchaseOrder.deleteAndReupload")}
                </Button>
              )}
              {canRetry && (
                <Button
                  disabled={actionLoading}
                  onClick={handleGenerate}
                  variant="outlined"
                >
                  {t("createPurchaseOrderByQuotation.runAgain")}
                </Button>
              )}
            </Box>
          )}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              onChange={(_event, value: string) => setTab(value)}
              value={tab}
            >
              <Tab
                label={t("spotPurchaseOrder.previewItemsCount", {
                  count: parsed.data.spot_items.length,
                })}
                value="items"
              />
              <Tab
                label={t("createPurchaseOrderByQuotation.executions")}
                value="executions"
              />
            </Tabs>
          </Box>
          <TabPanel value="items">
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("spotPurchaseOrder.sapStatusLabel")}
                    </TableCell>
                    <TableCell>{t("spotPurchaseOrder.rfqNumber")}</TableCell>
                    <TableCell>{t("spotPurchaseOrder.prNumber")}</TableCell>
                    <TableCell>{t("spotPurchaseOrder.poNumber")}</TableCell>
                    <TableCell>
                      {t("spotPurchaseOrder.materialNumber")}
                    </TableCell>
                    <TableCell>{t("spotPurchaseOrder.description")}</TableCell>
                    <TableCell>{t("common.vendor")}</TableCell>
                    <TableCell>{t("common.plant")}</TableCell>
                    <TableCell>{t("common.incoterm")}</TableCell>
                    <TableCell>{t("spotPurchaseOrder.taxCode")}</TableCell>
                    <TableCell>{t("spotPurchaseOrder.ncm")}</TableCell>
                    <TableCell>
                      {t("spotPurchaseOrder.materialOrigin")}
                    </TableCell>
                    <TableCell>{t("spotPurchaseOrder.leadTimeDays")}</TableCell>
                    <TableCell>{t("common.quantity")}</TableCell>
                    <TableCell>{t("common.unitPrice")}</TableCell>
                    <TableCell>{t("common.totalValue")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.data.spot_items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box
                          sx={{
                            color:
                              item.sap_status === "failed"
                                ? "error.main"
                                : undefined,
                          }}
                        >
                          {t(`spotPurchaseOrder.sapStatus.${item.sap_status}`)}
                        </Box>
                        {!!item.sap_error && (
                          <Box
                            component="span"
                            sx={{ color: "error.main", fontSize: 12 }}
                          >
                            {item.sap_error}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{item.rfq_number}</TableCell>
                      <TableCell>
                        {item.pr_number} / {item.item_number}
                      </TableCell>
                      <TableCell>
                        {item.purchase_order_number || "-"} /{" "}
                        {item.purchase_order_item_number ?? "-"}
                      </TableCell>
                      <TableCell>{item.material_number || "-"}</TableCell>
                      <TableCell>{item.description || "-"}</TableCell>
                      <TableCell>{item.vendor}</TableCell>
                      <TableCell>{item.plant || "-"}</TableCell>
                      <TableCell>{item.incoterm}</TableCell>
                      <TableCell>{item.tax_code}</TableCell>
                      <TableCell>{item.ncm || "-"}</TableCell>
                      <TableCell>{item.material_origin}</TableCell>
                      <TableCell>{item.lead_time_days}</TableCell>
                      <TableCell>
                        {formatNumber(item.quantity, i18n.language)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {item.currency}{" "}
                        {formatNumber(item.net_price, i18n.language)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {item.currency}{" "}
                        {formatNumber(item.total_value, i18n.language)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value="executions">
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.createdAt")}
                    </TableCell>
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.updatedAt")}
                    </TableCell>
                    <TableCell>{t("spotPurchaseOrder.initiatedBy")}</TableCell>
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.completed")}
                    </TableCell>
                    <TableCell>
                      {t("createPurchaseOrderByQuotationExecution.success")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.data.create_purchase_order_by_spot_executions.map(
                    (execution) => (
                      <Fragment key={execution.id}>
                        <TableRow>
                          <TableCell>
                            {Object.keys(execution.api_errors).length > 0 && (
                              <IconButton
                                aria-controls={`execution-errors-${execution.id}`}
                                aria-expanded={
                                  expandedExecution === execution.id
                                }
                                aria-label={t("details")}
                                onClick={() =>
                                  setExpandedExecution(
                                    expandedExecution === execution.id
                                      ? null
                                      : execution.id,
                                  )
                                }
                                size="small"
                              >
                                <Icon
                                  icon={
                                    expandedExecution === execution.id
                                      ? mdiChevronUp
                                      : mdiChevronDown
                                  }
                                />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            {t(
                              "intlDateTime",
                              i18nextParseToDate(execution.created_at),
                            )}
                          </TableCell>
                          <TableCell>
                            {t(
                              "intlDateTime",
                              i18nextParseToDate(execution.updated_at),
                            )}
                          </TableCell>
                          <TableCell>{execution.initiated_by}</TableCell>
                          <TableCell>
                            {execution.completed ? t("yes") : t("no")}
                          </TableCell>
                          <TableCell>
                            {execution.success ? t("yes") : t("no")}
                          </TableCell>
                        </TableRow>
                        {Object.keys(execution.api_errors).length > 0 && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0 }}>
                              <Collapse
                                id={`execution-errors-${execution.id}`}
                                in={expandedExecution === execution.id}
                                unmountOnExit
                              >
                                <ApiErrorsTable
                                  apiErrors={execution.api_errors as ApiErrors}
                                />
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </TabContext>
      )}
      {!!isSapReady && (
        <SpotPurchaseOrderPreviewModal
          actionError={actionError}
          actionLoading={actionLoading}
          canGenerate={canGenerate}
          createPurchaseOrderBySpotId={createPurchaseOrderBySpotId}
          onClose={() => setPreviewOpen(false)}
          onConfirm={handleGenerate}
          open={previewOpen}
        />
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/create_purchase_order_by_spots/$createPurchaseOrderBySpotId/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: spotSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({ itemsPerPage: 25, page: 1, upload: false }),
    ],
  },
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Spot - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ExecutionDetail,
});
